import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { GlassCard } from "../../components/ui/glass-card";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { apiClient } from "../../services/apiClient";
import { ResetPasswordRequest } from "../../generated/api-client";
import toast from "react-hot-toast";
import { PasswordRequirementsChecklist } from "../../components/PasswordRequirementsChecklist";
import { describeMissingRequirements, validatePassword } from "../../utils/passwordPolicy";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      toast.error("Token di reset non fornito");
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirm) return toast.error("Le password non coincidono");
    const missingRequirements = describeMissingRequirements(password);
    if (missingRequirements) return toast.error(missingRequirements);
    if (!token) return toast.error("Token di reset non valido");

    setLoading(true);
    try {
      const request = new ResetPasswordRequest({
        token,
        new_password: password,
        new_password_confirm: passwordConfirm,
      });
      await apiClient.reset_password_auth_reset_password_post(request);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Reset password fallito");
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "auto",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <GlassCard hover={false} className="w-full p-4 md:p-8 relative z-10 w-[28rem]">
        <div className="flex justify-center mb-6">
          <img src="/assets/long_light.png" alt="logo" className="h-12 rounded-lg" />
        </div>

        <Typography
          variant="h5"
          component="h1"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryLightColor})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Reset Password
        </Typography>

        {success ? (
          <Alert severity="success" sx={{ bgcolor: `${primaryColor}15` }}>
            Password resettata con successo! Reindirizzamento al login...
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              id="password"
              label="Nuova Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: "8px" } } }}
            />

            {password.length > 0 && <PasswordRequirementsChecklist password={password} />}

            <TextField
              id="passwordConfirm"
              label="Conferma Nuova Password"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: "8px" } } }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={
                loading ||
                !token ||
                !validatePassword(password).valid ||
                password !== passwordConfirm
              }
              fullWidth
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
                "&:hover": { filter: "brightness(0.9)" },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
            </Button>
          </Box>
        )}
      </GlassCard>

      <PoweredByFooter absolute />
    </Box>
  );
};

export default ResetPasswordPage;

